import React, { useState, useContext, useEffect } from 'react';
import { ServerConfigContext } from '../context/ServerConfigContext';
import { useAuth } from '../context/AuthContext';
import { useJWTAuth } from '../context/JWTAuthContext';
import { hasGradePermission } from '../utils/gradeSystem';
import RichTextEditor from '../components/RichTextEditor';
import './PostWritePage.css';

const PostWritePage = ({ boardId, currentBoard, onBack = () => {}, onPostCreated = () => {} }) => {
  const serverConfigContext = useContext(ServerConfigContext);
  const { getServer, isConfigLoaded } = serverConfigContext;
  const { userGrade } = useAuth();
  const { token: jwtToken, user: jwtUser, isAuthenticated: isJWTAuthenticated, isLoading: jwtLoading } = useJWTAuth();

  // JWT 상태 디버깅 (개발 모드에서만)
  if (process.env.NODE_ENV === 'development') {
    console.log('🔍 [PostWritePage] JWT 상태:', {
      jwtLoading,
      isJWTAuthenticated,
      hasUser: !!jwtUser,
      userNickname: jwtUser?.nickname,
      userName: jwtUser?.username
    });
  }

  // 게시판 정보 상태
  const [boardData, setBoardData] = useState(null);
  const [permissionError, setPermissionError] = useState(null);

  // 작성자 표시 상태
  const [authorDisplay, setAuthorDisplay] = useState('익명');
  
  // Authorization 헤더 생성 (JWT 토큰 우선 사용)
  const getAuthHeaders = () => {
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    };
    
    // JWT 토큰이 있으면 우선 사용, 없으면 기존 AuthContext 토큰 사용  
    const authToken = jwtToken || (localStorage.getItem('auth_token'));
    
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
      console.log('🔑 [PostWritePage] Authorization 헤더 설정:', {
        tokenSource: jwtToken ? 'JWT' : 'AuthContext',
        tokenLength: authToken.length,
        tokenStart: authToken.substring(0, 20) + '...'
      });
    } else {
      console.warn('⚠️ [PostWritePage] 인증 토큰이 없습니다.');
    }
    
    return headers;
  };
  
  // 게시글 작성 상태
  const [postTitle, setPostTitle] = useState('');
  const [postContent, setPostContent] = useState('');
  const [postSubmitting, setPostSubmitting] = useState(false);
  
  // 문자열 ID를 서버 숫자 ID로 매핑
  const getBoardId = (boardKey) => {
    const boardMapping = {
      'free': 1,
      'qna': 2,
      'notice': 3,
      'frontend': 4,
      'backend': 5,
      'mobile': 6,
      'photo': 7,
      'music': 8,
      'game': 9
    };
    return boardMapping[boardKey] || boardKey;
  };

  // 작성자 정보 업데이트
  useEffect(() => {
    if (jwtLoading) {
      setAuthorDisplay('로딩 중...');
    } else if (isJWTAuthenticated && jwtUser) {
      const authorName = jwtUser.nickname || jwtUser.username || jwtUser.name || '사용자';
      setAuthorDisplay(authorName);
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ [PostWritePage] 작성자 정보 업데이트:', authorName);
      }
    } else {
      setAuthorDisplay('익명');
    }
  }, [jwtLoading, isJWTAuthenticated, jwtUser]);

  // 게시판 정보 및 권한 확인
  useEffect(() => {
    const fetchBoardInfo = async () => {
      try {
        const boardServer = getServer('board-api-server');
        if (!boardServer || !boardServer.isActive) {
          console.warn('⚠️ [PostWritePage] 게시판 서버가 비활성화됨');
          setPermissionError('게시판 서버에 연결할 수 없습니다.');
          return;
        }

        const boardIdNumber = getBoardId(currentBoard);
        const apiUrl = `/board-api/api/v1/boards/${boardIdNumber}`;
        
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: getAuthHeaders()
        });

        if (!response.ok) {
          throw new Error(`API 호출 실패: ${response.status}`);
        }

        const result = await response.json();
        if (result.success && result.data) {
          setBoardData(result.data);
          
          // 쓰기 권한 확인
          const currentUserGrade = (isJWTAuthenticated ? jwtUser?.grade : userGrade) || 'newbie';
          const canWrite = hasGradePermission(currentUserGrade, result.data.min_write);
          
          if (!canWrite) {
            setPermissionError(
              `이 게시판에 글을 작성하려면 ${result.data.min_write || 'newbie'} 등급 이상이어야 합니다.\n` +
              `현재 등급: ${currentUserGrade}`
            );
          }
        }
      } catch (error) {
        console.error('❌ [PostWritePage] 게시판 정보 로드 실패:', error);
        setPermissionError('게시판 정보를 불러올 수 없습니다.');
      }
    };

    if (isConfigLoaded && !jwtLoading) {
      fetchBoardInfo();
    }
  }, [isConfigLoaded, currentBoard, userGrade, isJWTAuthenticated, jwtUser, jwtLoading]);

  // 게시글 작성
  const submitPost = async () => {
    if (!postTitle.trim() || !postContent.trim()) {
      alert('제목과 내용을 모두 입력해주세요.');
      return;
    }

    // 현재 로그인한 사용자 정보 가져오기
    const currentUser = isJWTAuthenticated ? jwtUser : null;
    const authorName = currentUser?.nickname || currentUser?.username || currentUser?.name || '익명';

    try {
      setPostSubmitting(true);
      
      const boardServer = getServer('board-api-server');
      if (!boardServer || !boardServer.isActive) {
        throw new Error('게시판 서버에 연결할 수 없습니다.');
      }

      const targetBoardId = getBoardId(currentBoard);
      const apiUrl = `/board-api/api/v1/boards/${targetBoardId}/posts`;
      if (process.env.NODE_ENV === 'development') {
        console.log('📝 [PostWritePage] 게시글 작성 API 호출:', apiUrl);
        console.log('👤 [PostWritePage] 작성자 정보:', { currentUser, authorName });
      }
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          title: postTitle.trim(),
          author: authorName,
          content: postContent.trim()
        })
      });

      if (!response.ok) {
        throw new Error(`게시글 작성 실패: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      
      if (result.success && result.data) {
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ [PostWritePage] 게시글 작성 완료:', result.data);
        }
        
        alert('게시글이 성공적으로 작성되었습니다!');
        
        // 새로 작성된 게시글 정보를 부모 컴포넌트에 전달
        onPostCreated(result.data);
        
        // 게시판 목록으로 돌아가기
        onBack();
      } else {
        throw new Error(result.message || '게시글 작성에 실패했습니다.');
      }
      
    } catch (error) {
      console.error('❌ [PostWritePage] 게시글 작성 실패:', error);
      alert('게시글 작성에 실패했습니다: ' + error.message);
    } finally {
      setPostSubmitting(false);
    }
  };

  // 게시판 이름 매핑
  const getBoardName = (boardKey) => {
    const boardNames = {
      'free': '자유게시판',
      'qna': 'Q&A',
      'notice': '공지사항',
      'frontend': '프론트엔드',
      'backend': '백엔드',
      'mobile': '모바일',
      'photo': '사진',
      'music': '음악',
      'game': '게임'
    };
    return boardNames[boardKey] || '게시판';
  };

  return (
    <div className="post-write-page">
      {/* 헤더 */}
      <div className="post-write-header">
        <button className="back-button" onClick={onBack}>
          ← 목록으로
        </button>
        <h1 className="page-title">📝 새 글쓰기 - {getBoardName(currentBoard)}</h1>
        <div></div>
      </div>

      {/* 권한 오류 표시 */}
      {permissionError && (
        <div className="permission-error">
          <div className="error-icon">🔒</div>
          <div className="error-message">
            <h3>게시글 작성 권한이 부족합니다</h3>
            <p>{permissionError}</p>
          </div>
          <button className="back-to-list-btn" onClick={onBack}>
            목록으로 돌아가기
          </button>
        </div>
      )}

      {/* 메인 폼 */}
      {!permissionError && (
        <div className="post-write-content">
        <div className="post-write-form">
          <div className="form-inputs">
            <div className="input-group">
              <label htmlFor="post-title">제목 *</label>
              <input
                id="post-title"
                type="text"
                value={postTitle}
                onChange={(e) => setPostTitle(e.target.value)}
                placeholder="게시글 제목을 입력하세요"
                disabled={postSubmitting}
              />
            </div>
            
            <div className="input-group">
              <label>작성자</label>
              <div className="author-info">
                👤 {authorDisplay}
                {isJWTAuthenticated && !jwtLoading && jwtUser && (
                  <span className="user-role">
                    ({jwtUser.role === 'admin' ? '관리자' : jwtUser.role === 'moderator' ? '운영자' : '일반회원'})
                  </span>
                )}
              </div>
            </div>
            
            <div className="input-group">
              <label htmlFor="post-content">내용 *</label>
              <RichTextEditor
                value={postContent}
                onChange={setPostContent}
                placeholder="게시글 내용을 입력하세요..."
                height="400px"
                disabled={postSubmitting}
              />
            </div>
          </div>
          
          <div className="form-actions">
            <button
              className="cancel-btn"
              onClick={onBack}
              disabled={postSubmitting}
            >
              취소
            </button>
            <button
              className="submit-btn"
              onClick={submitPost}
              disabled={postSubmitting || !postTitle.trim() || !postContent.trim()}
            >
              {postSubmitting ? '작성 중...' : '등록'}
            </button>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default PostWritePage;